import React from "react";
import ImgDialog from "../../dialog/ImgDialog";
import "../../dialog/imgStyle.css";

const TYPE_META = {
  multiple_choice: { label: "MCQ",        bg: "#EDE9FE", color: "#6D28D9" },
  boolean:         { label: "Yes / No",   bg: "#FEF9C3", color: "#92400E" },
  subjective:      { label: "Subjective", bg: "#DBEAFE", color: "#1D4ED8" },
};

const OPTION_LABELS = ["A", "B", "C", "D"];


function InterviewKitDialog({ selectedRowData, handleDialogClose }) {
  if (!selectedRowData) return null;

  const questions     = selectedRowData?.questions || [];
  const totalWeightage = selectedRowData?.total_weightage ?? 0;
  const maxWeightage  = questions.reduce((s, q) => s + (q.weightage || 0), 0);
  const overallPct    = maxWeightage > 0 ? Math.round((totalWeightage / maxWeightage) * 100) : 0;
  const overallColor  = overallPct >= 80 ? "#16A34A" : overallPct >= 50 ? "#F59E0B" : "#EF4444";

  return (
    <ImgDialog
      visible={selectedRowData !== null}
      handleCloseDialog={handleDialogClose}
      size="w-11/12"
    >
      {/* ── Outer wrapper: flex column, capped to 92 vh ── */}
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "92vh" }}>

        {/* ══════════ HEADER ══════════ */}
        <div style={{
          flexShrink: 0,
          background: "linear-gradient(135deg, #1E3A5F 0%, #2563EB 100%)",
          padding: "14px 20px 12px",
        }}>
          {/* Row 1: title + score summary inline */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "0 0 4px" }}>Interview Kit Response</h2>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "#BFDBFE" }}>Applicant ID: <strong style={{ color: "#fff" }}>{selectedRowData.job_applicant_id}</strong></span>
                <span style={{ fontSize: 11, color: "#BFDBFE" }}>Job ID: <strong style={{ color: "#fff" }}>{selectedRowData.job_id}</strong></span>
                <span style={{ fontSize: 11, color: "#BFDBFE" }}>Kit ID: <strong style={{ color: "#fff" }}>{selectedRowData.interview_kit_id}</strong></span>
              </div>
            </div>

            {/* Compact overall score pill */}
            <div style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.3)",
              borderRadius: 8, flexShrink: 0, padding: "8px 16px", textAlign: "center",
            }}>
              <div style={{ fontSize: 9, color: "#BFDBFE", fontWeight: 700, letterSpacing: 1, marginBottom: 3 }}>OVERALL SCORE</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4, justifyContent: "center" }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{totalWeightage}</span>
                <span style={{ fontSize: 13, color: "#BFDBFE" }}>/ {maxWeightage} pts</span>
              </div>
              <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.2)", overflow: "hidden", width: 110 }}>
                <div style={{ height: "100%", width: `${overallPct}%`, background: "#fff", borderRadius: 2 }} />
              </div>
              <div style={{ fontSize: 10, fontWeight: 700, marginTop: 3,
                color: overallColor === "#16A34A" ? "#6EE7B7" : overallColor === "#F59E0B" ? "#FDE68A" : "#FCA5A5",
              }}>
                {overallPct}%
              </div>
            </div>
          </div>

          {/* Row 2: compact stats strip */}
          <div style={{ display: "flex", gap: 8 }}>
            {[
              { label: "Questions",     value: questions.length },
              { label: "Response Weightage", value: `${totalWeightage} pts` },
              { label: "Max Weightage",     value: `${maxWeightage} pts` },
            ].map(stat => (
              <div key={stat.label} style={{
                background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)",
                borderRadius: 6, padding: "4px 12px", textAlign: "center",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{stat.value}</div>
                <div style={{ fontSize: 9, color: "#BFDBFE", fontWeight: 500 }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ══════════ BODY (scrollable) ══════════ */}
        <div style={{ flex: 1, overflowY: "auto", background: "#F1F5F9", padding: "16px 20px" }}>
          {questions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 0", color: "#94A3B8", fontSize: 14 }}>
              No responses available
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {questions.map((item, index) => {
                const meta = TYPE_META[item.question_type] || { label: item.question_type, bg: "#F1F5F9", color: "#64748B" };
                const rw   = item.response_weightage ?? 0;
                const tw   = item.weightage ?? 0;
                const pct  = tw > 0 ? Math.round((rw / tw) * 100) : 0;
                const scoreColor = pct === 100 ? "#16A34A" : pct >= 60 ? "#2563EB" : pct > 0 ? "#F59E0B" : "#EF4444";

                return (
                  <div key={index} style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>

                    {/* ── Question title bar ── */}
                    <div style={{
                      display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                      gap: 12, padding: "14px 16px 12px",
                      borderBottom: "1px solid #E2E8F0", background: "#FAFAFA",
                    }}>
                      <div style={{ display: "flex", gap: 10, flex: 1, minWidth: 0, alignItems: "flex-start" }}>
                        {/* Q number badge */}
                        <div style={{
                          flexShrink: 0, width: 28, height: 28, borderRadius: 6,
                          background: "#2563EB", display: "flex", alignItems: "center",
                          justifyContent: "center", fontSize: 12, fontWeight: 800, color: "#fff",
                        }}>
                          {(item.question_index ?? index) + 1}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: 14, color: "#0F172A", lineHeight: 1.55, fontWeight: 600, margin: 0 }}>
                            {item.question}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
                        {/* Max pts */}
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#2563EB", background: "#EFF6FF", border: "1px solid #BFDBFE", padding: "3px 10px", borderRadius: 5, whiteSpace: "nowrap" }}>
                          {tw} pts max
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, background: meta.bg, color: meta.color, padding: "2px 8px", borderRadius: 4 }}>
                          {meta.label}
                        </span>
                        {item.question_category && (
                          <span style={{ fontSize: 10, fontWeight: 600, background: "#F1F5F9", color: "#64748B", padding: "2px 7px", borderRadius: 4, whiteSpace: "nowrap" }}>
                            {item.question_category.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* ── Skills tags ── */}
                    {item.skills?.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", padding: "8px 16px 0" }}>
                        {item.skills.map((sk, si) => (
                          <span key={si} style={{ fontSize: 10, fontWeight: 600, background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE", padding: "2px 8px", borderRadius: 20 }}>
                            {sk}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* ── Content ── */}
                    <div style={{ padding: "12px 16px 14px" }}>

                      {/* MCQ */}
                      {item.question_type === "multiple_choice" && (
                        <div style={{ marginBottom: 12 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: "#64748B", margin: "0 0 7px", letterSpacing: 0.5 }}>
                            OPTIONS &amp; ANSWER
                          </p>
                          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                            {(item.options || []).map((opt, oi) => {
                              const isSelected = item.response === oi;
                              const isCorrect  = item.correct_option_index === oi;
                              return (
                                <div key={oi} style={{
                                  display: "flex", alignItems: "center", gap: 10,
                                  padding: "8px 12px", borderRadius: 7,
                                  border: isSelected ? "2px solid #2563EB" : isCorrect ? "2px solid #16A34A" : "1px solid #E2E8F0",
                                  background: isSelected ? "#EFF6FF" : isCorrect ? "#F0FDF4" : "#FAFAFA",
                                }}>
                                  {/* Circle */}
                                  <div style={{
                                    height: 24, width: 24, minWidth: 24, borderRadius: "50%",
                                    border: isSelected ? "2px solid #2563EB" : isCorrect ? "2px solid #16A34A" : "1.5px solid #CBD5E1",
                                    background: isSelected ? "#2563EB" : isCorrect ? "#16A34A" : "#fff",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 11, fontWeight: 700,
                                    color: (isSelected || isCorrect) ? "#fff" : "#64748B",
                                    flexShrink: 0,
                                  }}>
                                    {OPTION_LABELS[oi]}
                                  </div>
                                  {/* Text */}
                                  <div style={{ flex: 1, minWidth: 0, fontSize: 13, color: isSelected ? "#1D4ED8" : isCorrect ? "#15803D" : "#334155", lineHeight: 1.4 }}>
                                    {opt}
                                  </div>
                                  {/* Badges */}
                                  {isSelected && (
                                    <span style={{ fontSize: 10, fontWeight: 700, color: "#1D4ED8", background: "#DBEAFE", border: "1px solid #93C5FD", padding: "2px 8px", borderRadius: 4, flexShrink: 0 }}>
                                      Selected
                                    </span>
                                  )}
                                  {isCorrect && (
                                    <span style={{ fontSize: 10, fontWeight: 700, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", padding: "2px 8px", borderRadius: 4, flexShrink: 0 }}>
                                      ✓ Correct
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                            {(!item.options || item.options.length === 0) && (
                              <div style={{ fontSize: 13, color: "#1D4ED8", background: "#EFF6FF", border: "2px solid #2563EB", borderRadius: 7, padding: "8px 12px" }}>
                                Selected: Option {OPTION_LABELS[item.response] ?? item.response}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Boolean */}
                      {item.question_type === "boolean" && (
                        <div style={{ marginBottom: 12 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: "#64748B", margin: "0 0 7px", letterSpacing: 0.5 }}>ANSWER</p>
                          <div style={{ display: "flex", gap: 10 }}>
                            {[true, false].map(val => {
                              const isSelected = item.response === val;
                              const isCorrect  = item.correct_answer === val;
                              return (
                                <div key={String(val)} style={{
                                  flex: 1, padding: "10px 0", borderRadius: 7, textAlign: "center",
                                  border: isSelected ? "2px solid #2563EB" : isCorrect ? "2px solid #16A34A" : "1px solid #E2E8F0",
                                  background: isSelected ? "#EFF6FF" : isCorrect ? "#F0FDF4" : "#FAFAFA",
                                }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? "#1D4ED8" : isCorrect ? "#15803D" : "#475569" }}>
                                    {val ? "Yes (True)" : "No (False)"}
                                  </div>
                                  <div style={{ marginTop: 4, display: "flex", justifyContent: "center", gap: 4 }}>
                                    {isSelected && (
                                      <span style={{ fontSize: 10, fontWeight: 700, color: "#1D4ED8", background: "#DBEAFE", border: "1px solid #93C5FD", padding: "1px 6px", borderRadius: 4 }}>Selected</span>
                                    )}
                                    {isCorrect && (
                                      <span style={{ fontSize: 10, fontWeight: 700, color: "#15803D", background: "#DCFCE7", border: "1px solid #86EFAC", padding: "1px 6px", borderRadius: 4 }}>✓ Correct</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Subjective */}
                      {item.question_type === "subjective" && (
                        <div style={{ marginBottom: 12 }}>
                          <p style={{ fontSize: 11, fontWeight: 700, color: "#64748B", margin: "0 0 7px", letterSpacing: 0.5 }}>CANDIDATE RESPONSE</p>
                          <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 7, padding: "10px 13px", fontSize: 13, color: "#334155", lineHeight: 1.6, minHeight: 44, marginBottom: 8 }}>
                            {item.response || <span style={{ color: "#94A3B8" }}>No response provided</span>}
                          </div>
                          {item.expected_points?.filter(p => p).length > 0 && (
                            <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 7, padding: "10px 13px" }}>
                              <p style={{ fontSize: 11, fontWeight: 700, color: "#15803D", margin: "0 0 7px", letterSpacing: 0.5 }}>EXPECTED POINTS</p>
                              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                {item.expected_points.filter(p => p).map((pt, pi) => (
                                  <div key={pi} style={{ display: "flex", gap: 8, fontSize: 12, color: "#166534" }}>
                                    <span style={{ flexShrink: 0, fontWeight: 700 }}>•</span>
                                    <span>{pt}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* ── Score section ── */}
                      <div style={{ marginTop: 10, padding: "12px 14px", background: "#fff", border: `2px solid ${scoreColor}`, borderRadius: 8 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <b style={{ fontSize: 13, color: "#000" }}>Score</b>
                          <b style={{ fontSize: 16, color: scoreColor }}>{rw} / {tw} pts ({pct}%)</b>
                        </div>
                        <div style={{ height: 12, borderRadius: 6, background: "#E2E8F0", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: pct > 0 ? `${pct}%` : "3px", background: scoreColor, borderRadius: 6 }} />
                        </div>
                        {/* <div style={{ marginTop: 10, paddingTop: 10, borderTop: "2px solid #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <b style={{ fontSize: 12, color: "#000" }}>Contribution to total</b>
                          <b style={{ fontSize: 14, color: scoreColor }}>{rw} / {totalWeightage} pts ({totalWeightage > 0 ? Math.round((rw / totalWeightage) * 100) : 0}%)</b>
                        </div> */}
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <div style={{
          flexShrink: 0,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "12px 20px", borderTop: "1px solid #E2E8F0", background: "#fff",
        }}>
          <span style={{ fontSize: 12, color: "#64748B" }}>
            {questions.length} question{questions.length !== 1 ? "s" : ""} &nbsp;·&nbsp;
            <strong style={{ color: "#1E293B" }}>Total Weightage:</strong>{" "}
            <span style={{ fontWeight: 700, color: overallColor }}>{totalWeightage}</span>
            <span style={{ color: "#94A3B8" }}> / {maxWeightage} pts</span>
            <span style={{ fontWeight: 700, color: overallColor, marginLeft: 4 }}>({overallPct}%)</span>
          </span>
          <button
            onClick={handleDialogClose}
            style={{ background: "#1E3A5F", color: "#fff", border: "none", borderRadius: 6, padding: "9px 28px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
          >
            Close
          </button>
        </div>

      </div>
    </ImgDialog>
  );
}

export default InterviewKitDialog;
